import React, { useState, useEffect } from 'react';
import { Copy, Check, Package, Truck, Warehouse, Calendar, Calculator, Search, LogIn, UserPlus, List, TruckIcon } from 'lucide-react';

const PickupXpressAPIDocs = () => {
  const [activeSection, setActiveSection] = useState('login');
  const [copiedId, setCopiedId] = useState(null);
  const [module, setModule] = useState('b2b');

//   const copyToClipboard = (text, id, e) => {
//     if (e) {
//       e.preventDefault();
//       e.stopPropagation();
//     }
//     navigator.clipboard.writeText(text);
//     setCopiedId(id);
//     setTimeout(() => setCopiedId(null), 2000);
//   };

  const copyToClipboard = (text, id, e) => {
  e?.preventDefault();
  e?.stopPropagation();

  // THIS LINE FIXES MOST SCROLL JUMPS
  e?.currentTarget?.blur();

  navigator.clipboard.writeText(text).then(() => {
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  });
};


  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll('.api-section');
      let current = 'login';
      
      sections.forEach(section => {
        const sectionTop = section.offsetTop - 200;
        if (window.scrollY >= sectionTop) {
          current = section.id;
        }
      });
      
      setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(id);
    }
  };

  const CodeBlock = ({ code, id, title = "Request Body" }) => (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
      <div className="bg-gradient-to-r from-gray-800 to-gray-900 px-4 py-3 flex items-center justify-between">
        <span className="text-sm font-semibold text-white">{title}</span>
        <button
          onClick={(e) => copyToClipboard(code, id, e)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg text-xs transition-colors flex items-center gap-2"
          type="button"
        >
          {copiedId === id ? <Check size={14} /> : <Copy size={14} />}
          {copiedId === id ? 'Copied!' : 'Copy'}
        </button>
      </div>
      <div className="bg-gray-900 p-4 overflow-x-auto">
        <pre className="text-sm text-gray-100">
          <code>{code}</code>
        </pre>
      </div>
    </div>
  );

  const MethodBadge = ({ method }) => {
    const colors = {
      POST: 'bg-green-500',
      GET: 'bg-blue-500',
      PUT: 'bg-yellow-500',
      DELETE: 'bg-red-500'
    };
    
    return (
      <span className={`${colors[method] || 'bg-gray-500'} text-white px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wide`}>
        {method}
      </span>
    );
  };

  const ApiSection = ({ id, icon: Icon, iconColor, title, description, method, endpoint, headers, children, notes, module: sectionModule = 'all' }) => {
    if (module !== 'all' && sectionModule !== 'all' && module !== sectionModule) {
      return null;
    }

    return (
      <section id={id} className="api-section mb-8 scroll-mt-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Side - Details */}
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <div className="flex items-start gap-4 mb-4">
              <div className={`p-3 rounded-lg ${iconColor} bg-opacity-10`}>
                <Icon className={iconColor} size={28} />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">{title}</h2>
                {description && <p className="text-gray-600 leading-relaxed">{description}</p>}
              </div>
            </div>

            <div className="space-y-4 mt-6">
              <div>
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Endpoint</h3>
                <div className="flex items-center gap-3 flex-wrap">
                  <MethodBadge method={method} />
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <code className="bg-gray-50 px-4 py-2 rounded-lg text-sm text-gray-800 border border-gray-200 flex-1 min-w-0 break-all">
                      {endpoint}
                    </code>
                    <button
                      onClick={(e) => copyToClipboard(endpoint, `${id}-endpoint`, e)}
                      className="bg-gray-200 hover:bg-gray-300 text-gray-700 p-2 rounded-lg transition-colors flex-shrink-0"
                      title="Copy endpoint"
                      type="button"
                    >
                      {copiedId === `${id}-endpoint` ? <Check size={16} /> : <Copy size={16} />}
                    </button>
                  </div>
                </div>
              </div>

              {headers && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Headers Required</h3>
                  <div className="flex flex-wrap gap-2">
                    {headers.map((header, idx) => (
                      <code key={idx} className="bg-blue-50 text-blue-700 px-3 py-1.5 rounded-md text-sm font-medium border border-blue-200">
                        {header}
                      </code>
                    ))}
                  </div>
                </div>
              )}

              {notes && (
                <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-amber-800">{notes}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Side - Request Body */}
          <div>
            {children}
          </div>
        </div>
      </section>
    );
  };

  const navItems = [
    { id: 'login', label: 'Login', icon: LogIn, module: 'all' },
    { id: 'signup', label: 'Sign Up', icon: UserPlus, module: 'all' },
    { id: 'create-warehouse', label: 'Create Warehouse', icon: Warehouse, module: 'b2b' },
    { id: 'list-warehouse', label: 'List Warehouse', icon: List, module: 'b2b' },
    { id: 'create-shipment', label: 'Create Shipment', icon: Package, module: 'b2b' },
    { id: 'ship-shipment', label: 'Ship Shipment', icon: Package, module: 'b2b' },
    { id: 'list-shipment', label: 'List Shipment', icon: List, module: 'b2b' },
    { id: 'shipment-label', label: 'Shipment Label', icon: Package, module: 'b2b' },
    { id: 'shipment-waybill', label: 'Shipment Waybill', icon: Package, module: 'b2b' },
    { id: 'shipment-appointment', label: 'Appointment', icon: Calendar, module: 'b2b' },
    { id: 'create-pickup-request', label: 'Create Pickup Request', icon: TruckIcon, module: 'b2b' },
    { id: 'list-pickup-request', label: 'List Pickup Requests', icon: List, module: 'b2b' },
    { id: 'public-track', label: 'Public Track Shipment', icon: Search, module: 'b2b' },
    { id: 'track-shipment-b2b', label: 'B2B Track Shipment', icon: Truck, module: 'b2b' },
    { id: 'track-shipment', label: 'Track Shipment (Auth)', icon: Truck, module: 'b2b' },
    { id: 'book-appointment', label: 'Book Appointment', icon: Calendar, module: 'b2b' },
    { id: 'price-estimator', label: 'Price Estimator', icon: Calculator, module: 'b2b' },
    { id: 'b2c-track-shipment', label: 'Track Shipment (B2C)', icon: Truck, module: 'b2c' },
  ];

  const filteredNavItems = navItems.filter(item => 
    module === 'all' || item.module === 'all' || item.module === module
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white shadow-md z-50 border-b border-gray-200">
        <div className="flex items-center justify-between px-6 py-4">
          <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
            PickupXpress API Documentation
          </h1>
          <div className="flex items-center gap-4">
            <label className="font-semibold text-gray-700 text-sm">Module:</label>
            <select
              value={module}
              onChange={(e) => setModule(e.target.value)}
              className="px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-sm font-medium"
            >
              <option value="b2b">B2B</option>
              <option value="b2c">B2C</option>
              <option value="all">All</option>
            </select>
          </div>
          <span className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-semibold shadow-md">
            v1.0 - December 2025
          </span>
        </div>
      </header>

      <div className="flex pt-16">
        {/* Sidebar */}
        <aside className="fixed left-0 top-16 w-72 h-[calc(100vh-4rem)] bg-gradient-to-b from-gray-900 to-gray-800 overflow-y-auto shadow-xl">
          <div className="p-6 border-b border-gray-700">
            <img 
              src="https://retailer.pickupxpress.com/assets/images/logo1.png" 
              alt="PickupXpress"
              className="w-full h-auto"
            />
          </div>
          
          <nav className="p-4">
            {filteredNavItems.map((item, idx) => {
              const Icon = item.icon;
              return (
                <React.Fragment key={item.id}>
                  {idx === 2 && <div className="border-t border-gray-700 my-4" />}
                  <button
                    onClick={() => scrollToSection(item.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all mb-2 ${
                      activeSection === item.id
                        ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-lg translate-x-1'
                        : 'text-gray-300 hover:bg-gray-700 hover:text-white hover:translate-x-1'
                    }`}
                  >
                    <Icon size={20} />
                    <span className="text-sm font-medium">{item.label}</span>
                  </button>
                </React.Fragment>
              );
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="ml-72 flex-1 p-8 pb-20">
          <div className="max-w-7xl mx-auto">
            
            {/* Login */}
            <ApiSection
              id="login"
              icon={LogIn}
              iconColor="text-green-600"
              title="Login"
              description="Authenticate to get token and cus_id for all protected endpoints."
              method="POST"
              endpoint="https://api.pickupxpress.com/login"
              notes="Token expires after ~2 hours. Use returned token in headers: token and cus_id."
            >
              <CodeBlock
                id="login-code"
                code={`{
    "username": "sanjay@pickupxpress.com",
    "password": "Welcome@123"
}`}
              />
            </ApiSection>

            {/* Sign Up */}
            <ApiSection
              id="signup"
              icon={UserPlus}
              iconColor="text-blue-600"
              title="Sign Up"
              description="Register a new account. Body fields: name, email, password, phone, company_name (optional)."
              method="POST"
              endpoint="https://api.pickupxpress.com/register"
            >
              <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                <h3 className="text-lg font-semibold mb-3 text-gray-800">Body Parameters</h3>
                <ul className="space-y-2">
                  {['name', 'email', 'password', 'phone'].map(field => (
                    <li key={field} className="flex items-center gap-2 text-gray-700">
                      <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                      <code className="bg-gray-100 px-2 py-1 rounded text-sm">{field}</code>
                      <span className="text-red-500 text-sm">*</span>
                    </li>
                  ))}
                  <li className="flex items-center gap-2 text-gray-700">
                    <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
                    <code className="bg-gray-100 px-2 py-1 rounded text-sm">company_name</code>
                    <span className="text-gray-400 text-sm">(optional)</span>
                  </li>
                </ul>
              </div>
            </ApiSection>

            {/* Create Warehouse */}
            <ApiSection
              id="create-warehouse"
              icon={Warehouse}
              iconColor="text-purple-600"
              title="Create Warehouse"
              description="Create a new warehouse location for your business operations."
              method="POST"
              endpoint="https://api.pickupxpress.com/B2B/warehouse/create"
              headers={['cus_id', 'token']}
              module="b2b"
            >
              <CodeBlock
                id="create-warehouse-code"
                code={`{
    "pin_code": "201301",
    "city": "Noida",
    "state": "Uttar pradesh",
    "country": "India",
    "address_details": {
        "address": "C 702 ITHUM TOWEr",
        "contact_person": "Test",
        "phone_number": "9898989898"
    },
    "name": "lalittest ",
    "ret_address": {
        "pin": "110046",
        "address": "West Sagarpur "
    }
}`}
              />
            </ApiSection>

            {/* List Warehouse */}
            <ApiSection
              id="list-warehouse"
              icon={List}
              iconColor="text-indigo-600"
              title="List Warehouse"
              description="Retrieve all warehouses associated with your account."
              method="GET"
              endpoint="https://api.pickupxpress.com/B2B/warehouse/listAll"
              headers={['cus_id', 'token']}
              module="b2b"
            >
              <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl p-8 border border-indigo-200 text-center">
                <div className="inline-block p-4 bg-white rounded-full shadow-md mb-4">
                  <List className="text-indigo-600" size={32} />
                </div>
                <p className="text-gray-600 font-medium">No request body required</p>
                <p className="text-sm text-gray-500 mt-2">This is a GET request</p>
              </div>
            </ApiSection>

            {/* Create Shipment */}
            <ApiSection
              id="create-shipment"
              icon={Package}
              iconColor="text-blue-600"
              title="Create / Add Shipment"
              description="Use multipart/form-data to create or add shipments. You can attach documents, invoices, and label PDFs."
              method="POST"
              endpoint="https://api.pickupxpress.com/B2B/shipment/add"
              headers={['cus_id', 'token']}
              notes="JSON fields must be sent as strings. Keep 'insurence' spelling as-is (API requirement)."
              module="b2b"
            >
              <CodeBlock
                id="create-shipment-code"
                title="Multipart Form Data"
                code={`warehouse_id: PXB2BWH_1755862993
total_boxes: 2
payment_mode: prepaid
cod_amount: 0
total_weight: 20

dropoff_location: {"consignee_name":"test tets","address":"test address test","city":"Delhi","state":"Delhi","zip":"110001","phone":"9383883736","email":"test@gmail.com"}

invoices: [{"ewaybill":"8272872","inv_num":"82872","inv_amt":"2000","inv_qr_code":""}]

shipment_details: [{"order_id":"766","box_count":2,"description":"product desc","weight":20,"waybills":[],"master":"False"}]

dimensions: [{"box_count":2,"length":11,"width":11,"height":11}]

doc_data: [{"doc_type":"INVOICE_COPY","doc_meta":{"invoice_num":["766"]}}]

doc_file: (PDF file)

billing_address: {"name":"test tets","company":"pickupxpress","consignor":"test","address":"test address test","city":"Delhi","state":"Delhi","pin":"110001","phone":"9383883736","pan_number":"ABCDE1234F","gst_number":"09AAACH7409R1ZZ"}

insurence: Owner
fm_pickup: True
retailer_id: 2`}
              />
            </ApiSection>

            {/* Ship Shipment */}
            <ApiSection
              id="ship-shipment"
              icon={Package}
              iconColor="text-green-600"
              title="Ship Shipment"
              description="Process and ship an existing shipment."
              method="POST"
              endpoint="https://api.pickupxpress.com/B2B/shipment/create"
              headers={['cus_id', 'token']}
              module="b2b"
            >
              <CodeBlock
                id="ship-shipment-code"
                code={`{
    "order_id": "202601201730416",
    "shipment_type": "forword",
    "partner": "Delhivery"
}`}
              />
            </ApiSection>

            {/* List Shipment */}
            <ApiSection
              id="list-shipment"
              icon={List}
              iconColor="text-cyan-600"
              title="List Shipment"
              description="Retrieve all shipments from your account."
              method="GET"
              endpoint="https://api.pickupxpress.com/B2B/shipment/listAll"
              headers={['cus_id', 'token']}
              module="b2b"
            >
              <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl p-8 border border-cyan-200 text-center">
                <div className="inline-block p-4 bg-white rounded-full shadow-md mb-4">
                  <List className="text-cyan-600" size={32} />
                </div>
                <p className="text-gray-600 font-medium">No request body required</p>
                <p className="text-sm text-gray-500 mt-2">This is a GET request</p>
              </div>
            </ApiSection>

            {/* Shipment Label */}
            <ApiSection
              id="shipment-label"
              icon={Package}
              iconColor="text-violet-600"
              title="Shipment Label"
              description="Download shipment labels in A4 or 3by2 size formats."
              method="POST"
              endpoint="https://api.pickupxpress.com/B2B/shipment/label"
              headers={['cus_id', 'token']}
              module="b2b"
            >
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">A4 Size Label</h4>
                  <CodeBlock
                    id="label-a4-code"
                    title="Request Body (A4)"
                    code={`{
    "order_id": "202601201730416",
    "size": "A4"
}`}
                  />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">3by2 Size Label</h4>
                  <CodeBlock
                    id="label-3by2-code"
                    title="Request Body (3by2)"
                    code={`{
    "order_id": "202601201730416",
    "size": "3by2"
}`}
                  />
                </div>
              </div>
            </ApiSection>

            {/* Shipment Waybill */}
            <ApiSection
              id="shipment-waybill"
              icon={Package}
              iconColor="text-orange-600"
              title="Shipment Waybill"
              description="Generate and download waybill for your shipment."
              method="POST"
              endpoint="https://api.pickupxpress.com/B2B/shipment/waybill"
              headers={['cus_id', 'token']}
              module="b2b"
            >
              <CodeBlock
                id="waybill-code"
                code={`{
    "order_id": "202601201730416",
    "size": "A4"
}`}
              />
            </ApiSection>

            {/* Create Pickup Request */}
            <ApiSection
              id="create-pickup-request"
              icon={TruckIcon}
              iconColor="text-emerald-600"
              title="Create Pickup Request"
              description="Schedule a pickup for multiple orders from your warehouse."
              method="POST"
              endpoint="https://api.pickupxpress.com/B2B/shipment/PickupRequest/create"
              headers={['cus_id', 'token']}
              module="b2b"
            >
              <CodeBlock
                id="pickup-request-code"
                code={`{
    "warehouse_id": "PXB2BWH_1756118236",
    "pickup_date": "2025-11-19",
    "start_time": "05:30:22",
    "box_count": "10",
    "retailer_id": "211",
    "courier_partner": "delhivery",
    "order_ids": [
        "PXB2B202506030609254411",
        "PXB2B202506031153539538"
    ]
}`}
              />
            </ApiSection>

            {/* List Pickup Request */}
            <ApiSection
              id="list-pickup-request"
              icon={List}
              iconColor="text-teal-600"
              title="List Pickup Requests"
              description="View all scheduled pickup requests."
              method="GET"
              endpoint="https://api.pickupxpress.com/B2B/shipment/PickupRequest/list"
              headers={['cus_id', 'token']}
              module="b2b"
            >
              <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-8 border border-teal-200 text-center">
                <div className="inline-block p-4 bg-white rounded-full shadow-md mb-4">
                  <List className="text-teal-600" size={32} />
                </div>
                <p className="text-gray-600 font-medium">No request body required</p>
                <p className="text-sm text-gray-500 mt-2">This is a GET request</p>
              </div>
            </ApiSection>

            {/* Public Track */}
            <ApiSection
              id="public-track"
              icon={Search}
              iconColor="text-gray-700"
              title="Public Track Shipment"
              description="Track shipments without authentication. Perfect for customer-facing tracking."
              method="POST"
              endpoint="https://api.pickupxpress.com/public/B2B/track"
              notes="No authentication required for this endpoint."
              module="b2b"
            >
              <CodeBlock
                id="public-track-code"
                code={`{
    "order_id": "PXB2B202505310848273472",
    "partner": "Delhivery"
}`}
              />
            </ApiSection>

            {/* Track Shipment B2B */}
            <ApiSection
              id="track-shipment-b2b"
              icon={Truck}
              iconColor="text-amber-600"
              title="Track Shipment (Authenticated)"
              description="Track shipments with authentication for detailed information."
              method="POST"
              endpoint="https://api.pickupxpress.com/B2B/shipment/track"
              headers={['cus_id', 'token']}
              module="b2b"
            >
              <CodeBlock
                id="track-b2b-code"
                code={`{
    "order_id": "PXB2B202506030553088954",
    "partner": "Delhivery"
}`}
              />
            </ApiSection>

            {/* Track Shipment Auth */}
            <ApiSection
              id="track-shipment"
              icon={Truck}
              iconColor="text-blue-700"
              title="Shipment Track"
              description="Track shipment status and location with shipment type specification."
              method="POST"
              endpoint="https://api.pickupxpress.com/B2B/shipment/track"
              headers={['cus_id', 'token']}
              module="b2b"
            >
              <CodeBlock
                id="track-auth-code"
                code={`{
    "order_id": "2026012111574120",
    "shipment_type": "Forward"
}`}
              />
            </ApiSection>

            {/* Book Appointment */}
            <ApiSection
              id="book-appointment"
              icon={Calendar}
              iconColor="text-rose-600"
              title="Book Appointment"
              description="Schedule delivery appointments with time slots and PO numbers."
              method="POST"
              endpoint="https://api.pickupxpress.com/B2B/shipment/AppointmentBooking"
              headers={['cus_id', 'token']}
              module="b2b"
            >
              <CodeBlock
                id="appointment-code"
                code={`{
    "order_id": "PXB2B20250531084827347367",
    "start_date": "2026-11-20",
    "end_date": "2026-11-21",
    "time_slot": "09:10 - 12:00 morning",
    "po_number": ["122334"],
    "appointment_id": ""
}`}
              />
            </ApiSection>

            {/* Shipment Appointment */}
            <ApiSection
              id="shipment-appointment"
              icon={Calendar}
              iconColor="text-fuchsia-600"
              title="Shipment Appointment"
              description="Book or update appointment details for shipment delivery."
              method="POST"
              endpoint="https://api.pickupxpress.com/B2B/shipment/AppointmentBooking"
              headers={['cus_id', 'token']}
              module="b2b"
            >
              <CodeBlock
                id="shipment-appointment-code"
                code={`{
    "order_id": "202601201730416",
    "start_date": "2026-01-22",
    "end_date": "2026-01-22",
    "time_slot": "09:00 - 12:00 morning",
    "po_number": ["223"],
    "appointment_id": "22"
}`}
              />
            </ApiSection>

            {/* Price Estimator */}
            <ApiSection
              id="price-estimator"
              icon={Calculator}
              iconColor="text-sky-600"
              title="Price Estimator"
              description="Calculate shipping costs based on dimensions, weight, and distance."
              method="POST"
              endpoint="https://api.pickupxpress.com/B2B/price/estimator"
              headers={['cus_id', 'token']}
              module="b2b"
            >
              <CodeBlock
                id="price-estimator-code"
                code={`{
    "retailer_id": 211,
    "origin_pincode": "110001",
    "destination_pincode": "110001",
    "insorance": "Owner",
    "weight": 80,
    "invoice_value": 1000,
    "cft": "10",
    "dimensions": [
        {
            "quantity": 1,
            "total_length": 20,
            "total_width": 20,
            "total_height": 20
        }
    ]
}`}
              />
            </ApiSection>

            {/* B2C Track Shipment */}
            <ApiSection
              id="b2c-track-shipment"
              icon={Truck}
              iconColor="text-yellow-600"
              title="Track Shipment (B2C)"
              description="Track B2C shipments with order ID and shipment type."
              method="POST"
              endpoint="https://api.pickupxpress.com/V1/B2C/shipment/Track"
              headers={['cus_id', 'token']}
              module="b2c"
            >
              <CodeBlock
                id="b2c-track-code"
                code={`{
    "order_id": "358779476228",
    "shipment_type": "Forward"
}`}
              />
            </ApiSection>

          </div>
        </main>
      </div>
    </div>
  );
};

export default PickupXpressAPIDocs;










// import React, { useState, useEffect } from 'react';
// import { Copy, Check, Package, Truck, Warehouse, Calendar, Calculator, Search, LogIn, UserPlus, List, TruckIcon, Download } from 'lucide-react';

// const PickupXpressAPIDocs = () => {
//   const [activeSection, setActiveSection] = useState('login');
//   const [copiedId, setCopiedId] = useState(null);
//   const [module, setModule] = useState('b2b');

//   const copyToClipboard = (text, id, e) => {
//     if (e) {
//       e.preventDefault();
//       e.stopPropagation();
//     }
//     navigator.clipboard.writeText(text).then(() => {
//       setCopiedId(id);
//       setTimeout(() => setCopiedId(null), 2000);
//     });
//     return false;
//   };

//   useEffect(() => {
//     const handleScroll = () => {
//       const sections = document.querySelectorAll('.api-section');
//       let current = 'login';
      
//       sections.forEach(section => {
//         const sectionTop = section.offsetTop - 200;
//         if (window.scrollY >= sectionTop) {
//           current = section.id;
//         }
//       });
      
//       setActiveSection(current);
//     };

//     window.addEventListener('scroll', handleScroll);
//     return () => window.removeEventListener('scroll', handleScroll);
//   }, []);


//    const scrollToSection = (id) => {
//     const element = document.getElementById(id);
//     if (element) {
//       element.scrollIntoView({ behavior: 'smooth', block: 'start' });
//       setActiveSection(id);
//     }
//   };

//   const downloadPDF = async () => {
//     const element = document.getElementById('pdf-content');
    
//     // Create a printable version
//     const printWindow = window.open('', '_blank');
//     const content = `
//       <!DOCTYPE html>
//       <html>
//         <head>
//           <title>PickupXpress API Documentation</title>
//           <style>
//             body {
//               font-family: Arial, sans-serif;
//               padding: 40px;
//               line-height: 1.6;
//               color: #333;
//             }
//             h1 {
//               color: #4361ee;
//               border-bottom: 3px solid #4361ee;
//               padding-bottom: 10px;
//               margin-bottom: 30px;
//             }
//             h2 {
//               color: #2b2d42;
//               margin-top: 30px;
//               margin-bottom: 15px;
//               font-size: 24px;
//             }
//             h3 {
//               color: #555;
//               margin-top: 20px;
//               margin-bottom: 10px;
//             }
//             .section {
//               page-break-inside: avoid;
//               margin-bottom: 40px;
//               border: 1px solid #e0e0e0;
//               padding: 20px;
//               border-radius: 8px;
//             }
//             .endpoint {
//               background: #f5f5f5;
//               padding: 10px;
//               border-radius: 5px;
//               margin: 10px 0;
//               font-family: monospace;
//             }
//             .method {
//               display: inline-block;
//               padding: 5px 10px;
//               border-radius: 4px;
//               color: white;
//               font-weight: bold;
//               margin-right: 10px;
//             }
//             .method.post { background: #22c55e; }
//             .method.get { background: #3b82f6; }
//             code {
//               background: #1e1e2e;
//               color: #cdd6f4;
//               padding: 15px;
//               display: block;
//               border-radius: 5px;
//               overflow-x: auto;
//               white-space: pre;
//               margin: 10px 0;
//             }
//             .note {
//               background: #fef3c7;
//               border-left: 4px solid #f59e0b;
//               padding: 10px;
//               margin: 10px 0;
//             }
//             .header-info {
//               background: #dbeafe;
//               padding: 8px;
//               border-radius: 5px;
//               margin: 10px 0;
//             }
//             @media print {
//               .section {
//                 page-break-after: always;
//               }
//             }
//           </style>
//         </head>
//         <body>
//           <h1>PickupXpress API Documentation v1.0</h1>
//           <p><strong>Generated on:</strong> ${new Date().toLocaleDateString()}</p>
          
//           ${generateDocContent()}
//         </body>
//       </html>
//     `;
    
//     printWindow.document.write(content);
//     printWindow.document.close();
    
//     // Wait for content to load then trigger print
//     setTimeout(() => {
//       printWindow.print();
//     }, 500);
//   };

//   const generateDocContent = () => {
//     const sections = [
//       {
//         title: 'Login',
//         method: 'POST',
//         endpoint: 'https://api.pickupxpress.com/login',
//         description: 'Authenticate to get token and cus_id for all protected endpoints.',
//         note: 'Token expires after ~2 hours. Use returned token in headers: token and cus_id.',
//         body: `{
//     "username": "sanjay@pickupxpress.com",
//     "password": "Welcome@123"
// }`
//       },
//       {
//         title: 'Sign Up',
//         method: 'POST',
//         endpoint: 'https://api.pickupxpress.com/register',
//         description: 'Register a new account.',
//         body: 'Body fields: name, email, password, phone, company_name (optional).'
//       },
//       {
//         title: 'Create Warehouse (B2B)',
//         method: 'POST',
//         endpoint: 'https://api.pickupxpress.com/B2B/warehouse/create',
//         description: 'Create a new warehouse location for your business operations.',
//         headers: ['cus_id', 'token'],
//         body: `{
//     "pin_code": "201301",
//     "city": "Noida",
//     "state": "Uttar pradesh",
//     "country": "India",
//     "address_details": {
//         "address": "C 702 ITHUM TOWEr",
//         "contact_person": "Test",
//         "phone_number": "9898989898"
//     },
//     "name": "lalittest ",
//     "ret_address": {
//         "pin": "110046",
//         "address": "West Sagarpur "
//     }
// }`
//       },
//       {
//         title: 'List Warehouse (B2B)',
//         method: 'GET',
//         endpoint: 'https://api.pickupxpress.com/B2B/warehouse/listAll',
//         description: 'Retrieve all warehouses associated with your account.',
//         headers: ['cus_id', 'token'],
//         body: 'No request body required (GET request)'
//       },
//       {
//         title: 'Create/Add Shipment (B2B)',
//         method: 'POST',
//         endpoint: 'https://api.pickupxpress.com/B2B/shipment/add',
//         description: 'Use multipart/form-data to create or add shipments.',
//         headers: ['cus_id', 'token'],
//         note: "JSON fields must be sent as strings. Keep 'insurence' spelling as-is (API requirement).",
//         body: `warehouse_id: PXB2BWH_1755862993
// total_boxes: 2
// payment_mode: prepaid
// cod_amount: 0
// total_weight: 20
// dropoff_location: {"consignee_name":"test","address":"test address","city":"Delhi","state":"Delhi","zip":"110001","phone":"9383883736","email":"test@gmail.com"}
// invoices: [{"ewaybill":"8272872","inv_num":"82872","inv_amt":"2000"}]
// shipment_details: [{"order_id":"766","box_count":2,"description":"product desc","weight":20}]
// dimensions: [{"box_count":2,"length":11,"width":11,"height":11}]
// insurence: Owner
// fm_pickup: True
// retailer_id: 2`
//       },
//       {
//         title: 'Ship Shipment (B2B)',
//         method: 'POST',
//         endpoint: 'https://api.pickupxpress.com/B2B/shipment/create',
//         description: 'Process and ship an existing shipment.',
//         headers: ['cus_id', 'token'],
//         body: `{
//     "order_id": "202601201730416",
//     "shipment_type": "forword",
//     "partner": "Delhivery"
// }`
//       },
//       {
//         title: 'List Shipment (B2B)',
//         method: 'GET',
//         endpoint: 'https://api.pickupxpress.com/B2B/shipment/listAll',
//         description: 'Retrieve all shipments from your account.',
//         headers: ['cus_id', 'token'],
//         body: 'No request body required (GET request)'
//       },
//       {
//         title: 'Shipment Label (B2B)',
//         method: 'POST',
//         endpoint: 'https://api.pickupxpress.com/B2B/shipment/label',
//         description: 'Download shipment labels in A4 or 3by2 size formats.',
//         headers: ['cus_id', 'token'],
//         body: `A4 Size: {"order_id": "202601201730416", "size": "A4"}
// 3by2 Size: {"order_id": "202601201730416", "size": "3by2"}`
//       },
//       {
//         title: 'Shipment Waybill (B2B)',
//         method: 'POST',
//         endpoint: 'https://api.pickupxpress.com/B2B/shipment/waybill',
//         description: 'Generate and download waybill for your shipment.',
//         headers: ['cus_id', 'token'],
//         body: `{
//     "order_id": "202601201730416",
//     "size": "A4"
// }`
//       },
//       {
//         title: 'Create Pickup Request (B2B)',
//         method: 'POST',
//         endpoint: 'https://api.pickupxpress.com/B2B/shipment/PickupRequest/create',
//         description: 'Schedule a pickup for multiple orders from your warehouse.',
//         headers: ['cus_id', 'token'],
//         body: `{
//     "warehouse_id": "PXB2BWH_1756118236",
//     "pickup_date": "2025-11-19",
//     "start_time": "05:30:22",
//     "box_count": "10",
//     "retailer_id": "211",
//     "courier_partner": "delhivery",
//     "order_ids": ["PXB2B202506030609254411", "PXB2B202506031153539538"]
// }`
//       },
//       {
//         title: 'List Pickup Requests (B2B)',
//         method: 'GET',
//         endpoint: 'https://api.pickupxpress.com/B2B/shipment/PickupRequest/list',
//         description: 'View all scheduled pickup requests.',
//         headers: ['cus_id', 'token'],
//         body: 'No request body required (GET request)'
//       },
//       {
//         title: 'Public Track Shipment',
//         method: 'POST',
//         endpoint: 'https://api.pickupxpress.com/public/B2B/track',
//         description: 'Track shipments without authentication.',
//         note: 'No authentication required for this endpoint.',
//         body: `{
//     "order_id": "PXB2B202505310848273472",
//     "partner": "Delhivery"
// }`
//       },
//       {
//         title: 'Track Shipment (B2B)',
//         method: 'POST',
//         endpoint: 'https://api.pickupxpress.com/B2B/shipment/track',
//         description: 'Track shipments with authentication.',
//         headers: ['cus_id', 'token'],
//         body: `{
//     "order_id": "2026012111574120",
//     "shipment_type": "Forward"
// }`
//       },
//       {
//         title: 'Book Appointment (B2B)',
//         method: 'POST',
//         endpoint: 'https://api.pickupxpress.com/B2B/shipment/AppointmentBooking',
//         description: 'Schedule delivery appointments.',
//         headers: ['cus_id', 'token'],
//         body: `{
//     "order_id": "PXB2B20250531084827347367",
//     "start_date": "2026-11-20",
//     "end_date": "2026-11-21",
//     "time_slot": "09:10 - 12:00 morning",
//     "po_number": ["122334"],
//     "appointment_id": ""
// }`
//       },
//       {
//         title: 'Price Estimator (B2B)',
//         method: 'POST',
//         endpoint: 'https://api.pickupxpress.com/B2B/price/estimator',
//         description: 'Calculate shipping costs.',
//         headers: ['cus_id', 'token'],
//         body: `{
//     "retailer_id": 211,
//     "origin_pincode": "110001",
//     "destination_pincode": "110001",
//     "insorance": "Owner",
//     "weight": 80,
//     "invoice_value": 1000,
//     "cft": "10",
//     "dimensions": [{"quantity": 1, "total_length": 20, "total_width": 20, "total_height": 20}]
// }`
//       },
//       {
//         title: 'Track Shipment (B2C)',
//         method: 'POST',
//         endpoint: 'https://api.pickupxpress.com/V1/B2C/shipment/Track',
//         description: 'Track B2C shipments.',
//         headers: ['cus_id', 'token'],
//         body: `{
//     "order_id": "358779476228",
//     "shipment_type": "Forward"
// }`
//       }
//     ];

//     return sections.map(section => `
//       <div class="section">
//         <h2>${section.title}</h2>
//         <p>${section.description}</p>
//         <div class="endpoint">
//           <span class="method ${section.method.toLowerCase()}">${section.method}</span>
//           <span>${section.endpoint}</span>
//         </div>
//         ${section.headers ? `<div class="header-info"><strong>Required Headers:</strong> ${section.headers.join(', ')}</div>` : ''}
//         ${section.note ? `<div class="note"><strong>Note:</strong> ${section.note}</div>` : ''}
//         <h3>Request Body:</h3>
//         <code>${section.body}</code>
//       </div>
//     `).join('');
//   };

//   const CodeBlock = ({ code, id, title = "Request Body" }) => (
//     <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
//       <div className="bg-gradient-to-r from-gray-800 to-gray-900 px-4 py-3 flex items-center justify-between">
//         <span className="text-sm font-semibold text-white">{title}</span>
//         <button
//           onClick={(e) => copyToClipboard(code, id, e)}
//           className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg text-xs transition-colors flex items-center gap-2"
//           type="button"
//         >
//           {copiedId === id ? <Check size={14} /> : <Copy size={14} />}
//           {copiedId === id ? 'Copied!' : 'Copy'}
//         </button>
//       </div>
//       <div className="bg-gray-900 p-4 overflow-x-auto">
//         <pre className="text-sm text-gray-100">
//           <code>{code}</code>
//         </pre>
//       </div>
//     </div>
//   );

//   const MethodBadge = ({ method }) => {
//     const colors = {
//       POST: 'bg-green-500',
//       GET: 'bg-blue-500',
//       PUT: 'bg-yellow-500',
//       DELETE: 'bg-red-500'
//     };
    
//     return (
//       <span className={`${colors[method] || 'bg-gray-500'} text-white px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wide`}>
//         {method}
//       </span>
//     );
//   };

//   const ApiSection = ({ id, icon: Icon, iconColor, title, description, method, endpoint, headers, children, notes, module: sectionModule = 'all' }) => {
//     if (module !== 'all' && sectionModule !== 'all' && module !== sectionModule) {
//       return null;
//     }

//     return (
//       <section id={id} className="api-section mb-8 scroll-mt-24">
//         <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
//           {/* Left Side - Details */}
//           <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
//             <div className="flex items-start gap-4 mb-4">
//               <div className={`p-3 rounded-lg ${iconColor} bg-opacity-10`}>
//                 <Icon className={iconColor} size={28} />
//               </div>
//               <div className="flex-1">
//                 <h2 className="text-2xl font-bold text-gray-900 mb-2">{title}</h2>
//                 {description && <p className="text-gray-600 leading-relaxed">{description}</p>}
//               </div>
//             </div>

//             <div className="space-y-4 mt-6">
//               <div>
//                 <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Endpoint</h3>
//                 <div className="flex items-center gap-3 flex-wrap">
//                   <MethodBadge method={method} />
//                   <div className="flex items-center gap-2 flex-1 min-w-0">
//                     <code className="bg-gray-50 px-4 py-2 rounded-lg text-sm text-gray-800 border border-gray-200 flex-1 min-w-0 break-all">
//                       {endpoint}
//                     </code>
//                     <button
//                       onClick={(e) => copyToClipboard(endpoint, `${id}-endpoint`, e)}
//                       className="bg-gray-200 hover:bg-gray-300 text-gray-700 p-2 rounded-lg transition-colors flex-shrink-0"
//                       title="Copy endpoint"
//                       type="button"
//                     >
//                       {copiedId === `${id}-endpoint` ? <Check size={16} /> : <Copy size={16} />}
//                     </button>
//                   </div>
//                 </div>
//               </div>

//               {headers && (
//                 <div>
//                   <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Headers Required</h3>
//                   <div className="flex flex-wrap gap-2">
//                     {headers.map((header, idx) => (
//                       <code key={idx} className="bg-blue-50 text-blue-700 px-3 py-1.5 rounded-md text-sm font-medium border border-blue-200">
//                         {header}
//                       </code>
//                     ))}
//                   </div>
//                 </div>
//               )}

//               {notes && (
//                 <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg">
//                   <div className="flex">
//                     <div className="flex-shrink-0">
//                       <svg className="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
//                         <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
//                       </svg>
//                     </div>
//                     <div className="ml-3">
//                       <p className="text-sm font-medium text-amber-800">{notes}</p>
//                     </div>
//                   </div>
//                 </div>
//               )}
//             </div>
//           </div>

//           {/* Right Side - Request Body */}
//           <div>
//             {children}
//           </div>
//         </div>
//       </section>
//     );
//   };

//   const navItems = [
//     { id: 'login', label: 'Login', icon: LogIn, module: 'all' },
//     { id: 'signup', label: 'Sign Up', icon: UserPlus, module: 'all' },
//     { id: 'create-warehouse', label: 'Create Warehouse', icon: Warehouse, module: 'b2b' },
//     { id: 'list-warehouse', label: 'List Warehouse', icon: List, module: 'b2b' },
//     { id: 'create-shipment', label: 'Create Shipment', icon: Package, module: 'b2b' },
//     { id: 'ship-shipment', label: 'Ship Shipment', icon: Package, module: 'b2b' },
//     { id: 'list-shipment', label: 'List Shipment', icon: List, module: 'b2b' },
//     { id: 'shipment-label', label: 'Shipment Label', icon: Package, module: 'b2b' },
//     { id: 'shipment-waybill', label: 'Shipment Waybill', icon: Package, module: 'b2b' },
//     { id: 'shipment-appointment', label: 'Appointment', icon: Calendar, module: 'b2b' },
//     { id: 'create-pickup-request', label: 'Create Pickup Request', icon: TruckIcon, module: 'b2b' },
//     { id: 'list-pickup-request', label: 'List Pickup Requests', icon: List, module: 'b2b' },
//     { id: 'public-track', label: 'Public Track Shipment', icon: Search, module: 'b2b' },
//     { id: 'track-shipment-b2b', label: 'B2B Track Shipment', icon: Truck, module: 'b2b' },
//     { id: 'track-shipment', label: 'Track Shipment (Auth)', icon: Truck, module: 'b2b' },
//     { id: 'book-appointment', label: 'Book Appointment', icon: Calendar, module: 'b2b' },
//     { id: 'price-estimator', label: 'Price Estimator', icon: Calculator, module: 'b2b' },
//     { id: 'b2c-track-shipment', label: 'Track Shipment (B2C)', icon: Truck, module: 'b2c' },
//   ];

//   const filteredNavItems = navItems.filter(item => 
//     module === 'all' || item.module === 'all' || item.module === module
//   );

//   return (
//     <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
//       {/* Header */}
//       <header className="fixed top-0 left-0 right-0 bg-white shadow-md z-50 border-b border-gray-200">
//         <div className="flex items-center justify-between px-6 py-4">
//           <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
//             PickupXpress API Documentation
//           </h1>
//           <div className="flex items-center gap-4">
//             <label className="font-semibold text-gray-700 text-sm">Module:</label>
//             <select
//               value={module}
//               onChange={(e) => setModule(e.target.value)}
//               className="px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-sm font-medium"
//             >
//               <option value="b2b">B2B</option>
//               <option value="b2c">B2C</option>
//               <option value="all">All</option>
//             </select>
//             <button
//               onClick={downloadPDF}
//               className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-2 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all shadow-md text-sm font-semibold"
//               type="button"
//             >
//               <Download size={18} />
//               Download PDF
//             </button>
//           </div>
//           <span className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-semibold shadow-md">
//             v1.0 - December 2025
//           </span>
//         </div>
//       </header>

//       <div className="flex pt-16">
//         {/* Sidebar */}
//         <aside className="fixed left-0 top-16 w-72 h-[calc(100vh-4rem)] bg-gradient-to-b from-gray-900 to-gray-800 overflow-y-auto shadow-xl">
//           <div className="p-6 border-b border-gray-700">
//             <img 
//               src="https://retailer.pickupxpress.com/assets/images/logo1.png" 
//               alt="PickupXpress"
//               className="w-full h-auto"
//             />
//           </div>
          
//           <nav className="p-4">
//             {filteredNavItems.map((item, idx) => {
//               const Icon = item.icon;
//               return (
//                 <React.Fragment key={item.id}>
//                   {idx === 2 && <div className="border-t border-gray-700 my-4" />}
//                   <button
//                     onClick={(e) => scrollToSection(item.id, e)}
//                     className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all mb-2 ${
//                       activeSection === item.id
//                         ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-lg translate-x-1'
//                         : 'text-gray-300 hover:bg-gray-700 hover:text-white hover:translate-x-1'
//                     }`}
//                     type="button"
//                   >
//                     <Icon size={20} />
//                     <span className="text-sm font-medium">{item.label}</span>
//                   </button>
//                 </React.Fragment>
//               );
//             })}
//           </nav>
//         </aside>

//         {/* Main Content */}
//         <main className="ml-72 flex-1 p-8 pb-20" id="pdf-content">
//           <div className="max-w-7xl mx-auto">
            
//             {/* Login */}
//             <ApiSection
//               id="login"
//               icon={LogIn}
//               iconColor="text-green-600"
//               title="Login"
//               description="Authenticate to get token and cus_id for all protected endpoints."
//               method="POST"
//               endpoint="https://api.pickupxpress.com/login"
//               notes="Token expires after ~2 hours. Use returned token in headers: token and cus_id."
//             >
//               <CodeBlock
//                 id="login-code"
//                 code={`{
//     "username": "sanjay@pickupxpress.com",
//     "password": "Welcome@123"
// }`}
//               />
//             </ApiSection>

//             {/* Sign Up */}
//             <ApiSection
//               id="signup"
//               icon={UserPlus}
//               iconColor="text-blue-600"
//               title="Sign Up"
//               description="Register a new account. Body fields: name, email, password, phone, company_name (optional)."
//               method="POST"
//               endpoint="https://api.pickupxpress.com/register"
//             >
//               <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
//                 <h3 className="text-lg font-semibold mb-3 text-gray-800">Body Parameters</h3>
//                 <ul className="space-y-2">
//                   {['name', 'email', 'password', 'phone'].map(field => (
//                     <li key={field} className="flex items-center gap-2 text-gray-700">
//                       <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
//                       <code className="bg-gray-100 px-2 py-1 rounded text-sm">{field}</code>
//                       <span className="text-red-500 text-sm">*</span>
//                     </li>
//                   ))}
//                   <li className="flex items-center gap-2 text-gray-700">
//                     <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
//                     <code className="bg-gray-100 px-2 py-1 rounded text-sm">company_name</code>
//                     <span className="text-gray-400 text-sm">(optional)</span>
//                   </li>
//                 </ul>
//               </div>
//             </ApiSection>

//             {/* Create Warehouse */}
//             <ApiSection
//               id="create-warehouse"
//               icon={Warehouse}
//               iconColor="text-purple-600"
//               title="Create Warehouse"
//               description="Create a new warehouse location for your business operations."
//               method="POST"
//               endpoint="https://api.pickupxpress.com/B2B/warehouse/create"
//               headers={['cus_id', 'token']}
//               module="b2b"
//             >
//               <CodeBlock
//                 id="create-warehouse-code"
//                 code={`{
//     "pin_code": "201301",
//     "city": "Noida",
//     "state": "Uttar pradesh",
//     "country": "India",
//     "address_details": {
//         "address": "C 702 ITHUM TOWEr",
//         "contact_person": "Test",
//         "phone_number": "9898989898"
//     },
//     "name": "lalittest ",
//     "ret_address": {
//         "pin": "110046",
//         "address": "West Sagarpur "
//     }
// }`}
//               />
//             </ApiSection>

//             {/* List Warehouse */}
//             <ApiSection
//               id="list-warehouse"
//               icon={List}
//               iconColor="text-indigo-600"
//               title="List Warehouse"
//               description="Retrieve all warehouses associated with your account."
//               method="GET"
//               endpoint="https://api.pickupxpress.com/B2B/warehouse/listAll"
//               headers={['cus_id', 'token']}
//               module="b2b"
//             >
//               <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl p-8 border border-indigo-200 text-center">
//                 <div className="inline-block p-4 bg-white rounded-full shadow-md mb-4">
//                   <List className="text-indigo-600" size={32} />
//                 </div>
//                 <p className="text-gray-600 font-medium">No request body required</p>
//                 <p className="text-sm text-gray-500 mt-2">This is a GET request</p>
//               </div>
//             </ApiSection>

//             {/* Create Shipment */}
//             <ApiSection
//               id="create-shipment"
//               icon={Package}
//               iconColor="text-blue-600"
//               title="Create / Add Shipment"
//               description="Use multipart/form-data to create or add shipments. You can attach documents, invoices, and label PDFs."
//               method="POST"
//               endpoint="https://api.pickupxpress.com/B2B/shipment/add"
//               headers={['cus_id', 'token']}
//               notes="JSON fields must be sent as strings. Keep 'insurence' spelling as-is (API requirement)."
//               module="b2b"
//             >
//               <CodeBlock
//                 id="create-shipment-code"
//                 title="Multipart Form Data"
//                 code={`warehouse_id: PXB2BWH_1755862993
// total_boxes: 2
// payment_mode: prepaid
// cod_amount: 0
// total_weight: 20

// dropoff_location: {"consignee_name":"test tets","address":"test address test","city":"Delhi","state":"Delhi","zip":"110001","phone":"9383883736","email":"test@gmail.com"}

// invoices: [{"ewaybill":"8272872","inv_num":"82872","inv_amt":"2000","inv_qr_code":""}]

// shipment_details: [{"order_id":"766","box_count":2,"description":"product desc","weight":20,"waybills":[],"master":"False"}]

// dimensions: [{"box_count":2,"length":11,"width":11,"height":11}]

// doc_data: [{"doc_type":"INVOICE_COPY","doc_meta":{"invoice_num":["766"]}}]

// doc_file: (PDF file)

// billing_address: {"name":"test tets","company":"pickupxpress","consignor":"test","address":"test address test","city":"Delhi","state":"Delhi","pin":"110001","phone":"9383883736","pan_number":"ABCDE1234F","gst_number":"09AAACH7409R1ZZ"}

// insurence: Owner
// fm_pickup: True
// retailer_id: 2`}
//               />
//             </ApiSection>

//             {/* Ship Shipment */}
//             <ApiSection
//               id="ship-shipment"
//               icon={Package}
//               iconColor="text-green-600"
//               title="Ship Shipment"
//               description="Process and ship an existing shipment."
//               method="POST"
//               endpoint="https://api.pickupxpress.com/B2B/shipment/create"
//               headers={['cus_id', 'token']}
//               module="b2b"
//             >
//               <CodeBlock
//                 id="ship-shipment-code"
//                 code={`{
//     "order_id": "202601201730416",
//     "shipment_type": "forword",
//     "partner": "Delhivery"
// }`}
//               />
//             </ApiSection>

//             {/* List Shipment */}
//             <ApiSection
//               id="list-shipment"
//               icon={List}
//               iconColor="text-cyan-600"
//               title="List Shipment"
//               description="Retrieve all shipments from your account."
//               method="GET"
//               endpoint="https://api.pickupxpress.com/B2B/shipment/listAll"
//               headers={['cus_id', 'token']}
//               module="b2b"
//             >
//               <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl p-8 border border-cyan-200 text-center">
//                 <div className="inline-block p-4 bg-white rounded-full shadow-md mb-4">
//                   <List className="text-cyan-600" size={32} />
//                 </div>
//                 <p className="text-gray-600 font-medium">No request body required</p>
//                 <p className="text-sm text-gray-500 mt-2">This is a GET request</p>
//               </div>
//             </ApiSection>

//             {/* Shipment Label */}
//             <ApiSection
//               id="shipment-label"
//               icon={Package}
//               iconColor="text-violet-600"
//               title="Shipment Label"
//               description="Download shipment labels in A4 or 3by2 size formats."
//               method="POST"
//               endpoint="https://api.pickupxpress.com/B2B/shipment/label"
//               headers={['cus_id', 'token']}
//               module="b2b"
//             >
//               <div className="space-y-4">
//                 <div>
//                   <h4 className="text-sm font-semibold text-gray-700 mb-2">A4 Size Label</h4>
//                   <CodeBlock
//                     id="label-a4-code"
//                     title="Request Body (A4)"
//                     code={`{
//     "order_id": "202601201730416",
//     "size": "A4"
// }`}
//                   />
//                 </div>
//                 <div>
//                   <h4 className="text-sm font-semibold text-gray-700 mb-2">3by2 Size Label</h4>
//                   <CodeBlock
//                     id="label-3by2-code"
//                     title="Request Body (3by2)"
//                     code={`{
//     "order_id": "202601201730416",
//     "size": "3by2"
// }`}
//                   />
//                 </div>
//               </div>
//             </ApiSection>

//             {/* Shipment Waybill */}
//             <ApiSection
//               id="shipment-waybill"
//               icon={Package}
//               iconColor="text-orange-600"
//               title="Shipment Waybill"
//               description="Generate and download waybill for your shipment."
//               method="POST"
//               endpoint="https://api.pickupxpress.com/B2B/shipment/waybill"
//               headers={['cus_id', 'token']}
//               module="b2b"
//             >
//               <CodeBlock
//                 id="waybill-code"
//                 code={`{
//     "order_id": "202601201730416",
//     "size": "A4"
// }`}
//               />
//             </ApiSection>

//             {/* Create Pickup Request */}
//             <ApiSection
//               id="create-pickup-request"
//               icon={TruckIcon}
//               iconColor="text-emerald-600"
//               title="Create Pickup Request"
//               description="Schedule a pickup for multiple orders from your warehouse."
//               method="POST"
//               endpoint="https://api.pickupxpress.com/B2B/shipment/PickupRequest/create"
//               headers={['cus_id', 'token']}
//               module="b2b"
//             >
//               <CodeBlock
//                 id="pickup-request-code"
//                 code={`{
//     "warehouse_id": "PXB2BWH_1756118236",
//     "pickup_date": "2025-11-19",
//     "start_time": "05:30:22",
//     "box_count": "10",
//     "retailer_id": "211",
//     "courier_partner": "delhivery",
//     "order_ids": [
//         "PXB2B202506030609254411",
//         "PXB2B202506031153539538"
//     ]
// }`}
//               />
//             </ApiSection>

//             {/* List Pickup Request */}
//             <ApiSection
//               id="list-pickup-request"
//               icon={List}
//               iconColor="text-teal-600"
//               title="List Pickup Requests"
//               description="View all scheduled pickup requests."
//               method="GET"
//               endpoint="https://api.pickupxpress.com/B2B/shipment/PickupRequest/list"
//               headers={['cus_id', 'token']}
//               module="b2b"
//             >
//               <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-8 border border-teal-200 text-center">
//                 <div className="inline-block p-4 bg-white rounded-full shadow-md mb-4">
//                   <List className="text-teal-600" size={32} />
//                 </div>
//                 <p className="text-gray-600 font-medium">No request body required</p>
//                 <p className="text-sm text-gray-500 mt-2">This is a GET request</p>
//               </div>
//             </ApiSection>

//             {/* Public Track */}
//             <ApiSection
//               id="public-track"
//               icon={Search}
//               iconColor="text-gray-700"
//               title="Public Track Shipment"
//               description="Track shipments without authentication. Perfect for customer-facing tracking."
//               method="POST"
//               endpoint="https://api.pickupxpress.com/public/B2B/track"
//               notes="No authentication required for this endpoint."
//               module="b2b"
//             >
//               <CodeBlock
//                 id="public-track-code"
//                 code={`{
//     "order_id": "PXB2B202505310848273472",
//     "partner": "Delhivery"
// }`}
//               />
//             </ApiSection>

//             {/* Track Shipment B2B */}
//             <ApiSection
//               id="track-shipment-b2b"
//               icon={Truck}
//               iconColor="text-amber-600"
//               title="Track Shipment (Authenticated)"
//               description="Track shipments with authentication for detailed information."
//               method="POST"
//               endpoint="https://api.pickupxpress.com/B2B/shipment/track"
//               headers={['cus_id', 'token']}
//               module="b2b"
//             >
//               <CodeBlock
//                 id="track-b2b-code"
//                 code={`{
//     "order_id": "PXB2B202506030553088954",
//     "partner": "Delhivery"
// }`}
//               />
//             </ApiSection>

//             {/* Track Shipment Auth */}
//             <ApiSection
//               id="track-shipment"
//               icon={Truck}
//               iconColor="text-blue-700"
//               title="Shipment Track"
//               description="Track shipment status and location with shipment type specification."
//               method="POST"
//               endpoint="https://api.pickupxpress.com/B2B/shipment/track"
//               headers={['cus_id', 'token']}
//               module="b2b"
//             >
//               <CodeBlock
//                 id="track-auth-code"
//                 code={`{
//     "order_id": "2026012111574120",
//     "shipment_type": "Forward"
// }`}
//               />
//             </ApiSection>

//             {/* Book Appointment */}
//             <ApiSection
//               id="book-appointment"
//               icon={Calendar}
//               iconColor="text-rose-600"
//               title="Book Appointment"
//               description="Schedule delivery appointments with time slots and PO numbers."
//               method="POST"
//               endpoint="https://api.pickupxpress.com/B2B/shipment/AppointmentBooking"
//               headers={['cus_id', 'token']}
//               module="b2b"
//             >
//               <CodeBlock
//                 id="appointment-code"
//                 code={`{
//     "order_id": "PXB2B20250531084827347367",
//     "start_date": "2026-11-20",
//     "end_date": "2026-11-21",
//     "time_slot": "09:10 - 12:00 morning",
//     "po_number": ["122334"],
//     "appointment_id": ""
// }`}
//               />
//             </ApiSection>

//             {/* Shipment Appointment */}
//             <ApiSection
//               id="shipment-appointment"
//               icon={Calendar}
//               iconColor="text-fuchsia-600"
//               title="Shipment Appointment"
//               description="Book or update appointment details for shipment delivery."
//               method="POST"
//               endpoint="https://api.pickupxpress.com/B2B/shipment/AppointmentBooking"
//               headers={['cus_id', 'token']}
//               module="b2b"
//             >
//               <CodeBlock
//                 id="shipment-appointment-code"
//                 code={`{
//     "order_id": "202601201730416",
//     "start_date": "2026-01-22",
//     "end_date": "2026-01-22",
//     "time_slot": "09:00 - 12:00 morning",
//     "po_number": ["223"],
//     "appointment_id": "22"
// }`}
//               />
//             </ApiSection>

//             {/* Price Estimator */}
//             <ApiSection
//               id="price-estimator"
//               icon={Calculator}
//               iconColor="text-sky-600"
//               title="Price Estimator"
//               description="Calculate shipping costs based on dimensions, weight, and distance."
//               method="POST"
//               endpoint="https://api.pickupxpress.com/B2B/price/estimator"
//               headers={['cus_id', 'token']}
//               module="b2b"
//             >
//               <CodeBlock
//                 id="price-estimator-code"
//                 code={`{
//     "retailer_id": 211,
//     "origin_pincode": "110001",
//     "destination_pincode": "110001",
//     "insorance": "Owner",
//     "weight": 80,
//     "invoice_value": 1000,
//     "cft": "10",
//     "dimensions": [
//         {
//             "quantity": 1,
//             "total_length": 20,
//             "total_width": 20,
//             "total_height": 20
//         }
//     ]
// }`}
//               />
//             </ApiSection>

//             {/* B2C Track Shipment */}
//             <ApiSection
//               id="b2c-track-shipment"
//               icon={Truck}
//               iconColor="text-yellow-600"
//               title="Track Shipment (B2C)"
//               description="Track B2C shipments with order ID and shipment type."
//               method="POST"
//               endpoint="https://api.pickupxpress.com/V1/B2C/shipment/Track"
//               headers={['cus_id', 'token']}
//               module="b2c"
//             >
//               <CodeBlock
//                 id="b2c-track-code"
//                 code={`{
//     "order_id": "358779476228",
//     "shipment_type": "Forward"
// }`}
//               />
//             </ApiSection>

//           </div>
//         </main>
//       </div>
//     </div>
//   );
// };

// export default PickupXpressAPIDocs;